import PropTypes from 'prop-types';
import React from 'react';
import useTranslatedText from '../../../hooks/useTranslatedText';
import BrandLinks from '../../Footer/FooterGrid/BrandLinks';
import HighContrastToggle from '../../HighContrastToggle/HighContrastToggle';
import { footerShape } from '../propTypesShapes';
import MenuGroup from './MenuGroup';
import MenuList from './MenuList';
import styles from './menu.module.scss';

/**
 * The L0 level menu with L1 links and the footer
 *
 * @param {Object} props - The props for the TopLevelMenu component.
 * @param {Array<Object>} props.primaryLinks - Array of L0 links.
 * @param {string} props.primaryLinks[].label - The name of the category.
 * @param {string} [props.primaryLinks[].link] - The URL, if the link
 * should not open another menu level.
 * @param {boolean} [props.primaryLinks[].hasSpecialNavInteraction] - Whether the
 * link title is an espot.
 * @param {string} [props.primaryLinks[].espotIdentifier] - Identifier for the espot
 * in place of the link title.
 * @param {Array<Object>} props.footerLinks - Array of footer links.
 * @param {Function} props.handleClick - Callback function triggered when a link is clicked.
 * @returns {JSX.Element} The rendered TopLevelMenu component.
 */
const TopLevelMenu = ({
  footerData,
  primaryLinks,
  handleClick,
}) => {
  const aboutUs = useTranslatedText('aboutUs', { fallback: 'About Us' });
  const help = useTranslatedText('help', { fallback: 'Help' });
  const privacy = useTranslatedText('privacy', { fallback: 'Privacy & Terms' });

  const footerLinks = [
    { id: 'helpLinks', label: help.value },
    { id: 'utilityLinks', label: privacy.value },
    { id: 'aboutLinks', label: aboutUs.value },
    {
      id: 'contactUs', label: footerData?.contactUs?.title, link: footerData?.contactUs?.url, external: footerData?.contactUs?.external,
    },
  ];

  const footerLogos = footerData?.footerLogos || [];

  return (
    <div className={styles.topLevelMenuContainer}>
      <div>
        <MenuGroup label="Primary Links">
          <MenuList
            dataTestId="category-list-item-regular"
            handleClick={handleClick}
            links={primaryLinks}
            theme="primary"
          />
        </MenuGroup>
        {/* Footer links */}
        <MenuGroup label="Footer Links">
          <MenuList
            handleClick={handleClick}
            links={footerLinks}
            theme="tertiary"
          />
          <div className={styles.tertiary}>
            <HighContrastToggle id="high-contrast-toggle-small-screen-nav" />
          </div>
        </MenuGroup>
      </div>
      {/* Footer Brand logos */}
      {footerLogos ? (
        <div>
          <div aria-label="Footer Brands Logo" className={styles.brandsLogoContainer}>
            <BrandLinks hasRefreshedNav isSmallNav links={footerLogos} />
          </div>
        </div>
      ) : null }
    </div>
  );
};

TopLevelMenu.propTypes = {
  primaryLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      link: PropTypes.string,
      hasSpecialNavInteraction: PropTypes.bool,
      espotIdentifier: PropTypes.string,
    }),
  ).isRequired,
  handleClick: PropTypes.func.isRequired,
  footerData: footerShape,
};

export default TopLevelMenu;
